import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, LayoutGrid, PanelLeft, Plus, FileText, Shield } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EnTete } from "@/components/commun";
import { formatDateHeure } from "@/lib/format";

const STATUTS = { actif: ["Actif", "var(--success)"], pause: ["En pause", "var(--temp-3)"], brouillon: ["Brouillon", "var(--muted)"] };
const PROFILS = [["responsable", "Responsable plateau"], ["agent", "Agent"], ["confirmateur", "Confirmateur"]];

const Tuile = ({ o }) => (
  <Link to={`/outils/${o.id}`} className="at-card p-5 flex flex-col gap-2 min-h-[150px] at-rise transition-transform duration-200 hover:-translate-y-0.5" data-testid={`tool-tile-${o.id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-bold break-words" data-testid={`tool-name-${o.id}`}>{o.nom}</p>
      <span className="text-xs font-semibold inline-flex items-center gap-1.5 shrink-0" data-testid={`tool-status-${o.id}`}><span className="w-2 h-2 at-pill" style={{ background: STATUTS[o.statut][1] }} />{STATUTS[o.statut][0]}</span>
    </div>
    <p className="text-sm at-muted line-clamp-2 break-words">{o.description}</p>
    <div className="mt-auto flex items-center gap-2 text-xs at-muted flex-wrap">
      <span>{formatDateHeure(o.updated_at)}</span>
      {o.type === "vue" && <span className="at-pill px-2 py-0.5 flex items-center gap-1" style={{ background: "var(--surface-2)" }}><LayoutGrid size={12} />Vue</span>}
      {o.modele && <span className="at-pill px-2 py-0.5" style={{ background: "var(--surface-2)" }}>Modèle</span>}
      {o.epingles?.accueil && <Home size={13} aria-label="Épinglé à l'accueil" />}{o.epingles?.fiche && <FileText size={13} aria-label="Épinglé dans la fiche" />}{o.epingles?.dock && <PanelLeft size={13} aria-label="Épinglé dans le dock" />}
    </div>
  </Link>
);

const Acces = () => {
  const [roles, setRoles] = useState(null);
  useEffect(() => { api.get("/outils/acces").then((r) => setRoles(r.data.roles)); }, []);
  if (!roles) return null;
  const basculer = async (r) => {
    const n = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
    try { const { data } = await api.put("/outils/acces", { roles: n.filter((x) => x !== "dirigeant") }); setRoles(data.roles); toast.success("Accès mis à jour."); }
    catch (x) { toast.error(messageErreur(x)); }
  };
  return (
    <details className="at-card p-4" data-testid="tools-access">
      <summary className="font-semibold min-h-[44px] flex items-center gap-2 cursor-pointer"><Shield size={16} />Qui peut utiliser les outils ? <span className="text-xs at-muted font-normal">(phase de test : Dirigeant seul)</span></summary>
      <div className="grid sm:grid-cols-3 gap-2 mt-2">
        {PROFILS.map(([r, l]) => (
          <label key={r} className="at-soft px-3 min-h-[44px] flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={roles.includes(r)} onChange={() => basculer(r)} className="w-5 h-5" data-testid={`tools-access-${r}`} />{l}
          </label>
        ))}
      </div>
    </details>
  );
};

export default function MesOutils() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const charger = useCallback(() => api.get("/outils").then((r) => setL(r.data)).catch((x) => toast.error(messageErreur(x))), []);
  useEffect(() => { charger(); }, [charger]);
  const dir = user.role === "dirigeant";
  return (
    <div className="space-y-5" data-testid="page-tools">
      <EnTete titre="Mes outils" sousTitre="Mini-applications créées par l'assistant (génération simulée)">
        {dir && <button type="button" onClick={() => navigate("/outils/nouveau")} className="at-btn at-btn-primary" data-testid="new-tool-button"><Plus size={16} />Nouvel outil</button>}
      </EnTete>
      {dir && <Acces />}
      {!l ? <p className="at-muted text-sm">Chargement…</p> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tools-grid">{l.map((o) => <Tuile key={o.id} o={o} />)}</div>
      )}
    </div>
  );
}
